import React, { useMemo } from 'react'

import { Avatar, Table, Typography } from 'components/common'
import { formatDateNumeric } from 'helpers'
import { useAppDispatch, useFrontOfficeCheck, useSortable } from 'hooks'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import styles from './MaterialsTable.module.scss'
import { OfficeName } from '../../../../../constants/application-constants'
import FileFormatIcon from '../../../Other/components/FileFormatIcon/FileFormatIcon'
import { removeMaterial, renameMaterial } from '../../api/currentMaterialSlice'
import { Material } from '../../api/materialsApi.types'
import MaterialsMorePopover from '../MaterialsMorePopover/MaterialsMorePopover'

interface MaterialsTableProps {
  materials: Material[]
  status?: string | null
  labelledBy?: string
  guidelineId?: string
}

type MaterialsTableSortBy = 'name' | 'type' | 'author' | 'updatedAt'

const MaterialsTable = ({
  materials,
  labelledBy,
  guidelineId,
}: MaterialsTableProps): JSX.Element => {
  const dispatch = useAppDispatch()
  const { sortBy, sortDir, handleSortClick } =
    useSortable<MaterialsTableSortBy>({
      sortByDefault: 'name',
      sortDirDefault: 'asc',
    })
  const { t } = useTranslation()
  const isFrontOffice = useFrontOfficeCheck()

  const onMaterialDelete = (id: string) => {
    dispatch(removeMaterial(id))
  }
  const onMaterialRename = (id: string, newName: string) => {
    dispatch(renameMaterial({ id, newName }))
  }

  const getMaterialTypeAsString = (mime: string, name: string) => {
    if (name?.endsWith('.ppt') || name?.endsWith('.pptx'))
      return t('filetype.powerpoint')
    if (name?.endsWith('.doc') || name?.endsWith('.docx'))
      return t('filetype.word')
    if (name?.endsWith('.xls') || name?.endsWith('.xlsx'))
      return t('filetype.excel')

    if (mime.startsWith('image')) return t('filetype.image')
    if (mime.startsWith('video')) return t('filetype.video')
    if (mime.endsWith('pdf')) return t('filetype.pdf')
    if (mime.startsWith('text')) return t('filetype.text')
    return 'Other'
  }

  const materialsTableData = useMemo(() => {
    const data = materials.map((material) => {
      return {
        name: material.name,
        updatedAt: material.updated_at,
        materialId: material.id,
        type: material.file.mime,
        author: material.author.name,
        initials: material.author.initials,
        avatar_public_path: material.author.avatar_public_path,
        fileId: material.file.id,
      }
    })

    if (sortBy) {
      return data.sort((a, b) => {
        const _a = a[sortBy]?.toLowerCase() ?? ''
        const _b = b[sortBy]?.toLowerCase() ?? ''
        if (_a < _b) {
          return sortDir === 'asc' ? -1 : 1
        } else if (_a > _b) {
          return sortDir === 'asc' ? 1 : -1
        } else {
          return 0
        }
      })
    }

    return data
  }, [materials, sortBy, sortDir])

  return (
    <>
      <Table isFullWidth className={styles.root} aria-labelledby={labelledBy}>
        <Table.Head>
          <Table.Row isHeadRow>
            <Table.Cell
              onClick={() => handleSortClick('name')}
              sortDir={sortDir}
              sortByThisColumn={sortBy === 'name'}
            >
              {t('documentsTable.head1')}
            </Table.Cell>
            <Table.Cell
              onClick={() => handleSortClick('type')}
              sortDir={sortDir}
              sortByThisColumn={sortBy === 'type'}
            >
              {t('documentsTable.head2')}
            </Table.Cell>
            <Table.Cell
              onClick={() => handleSortClick('author')}
              sortDir={sortDir}
              sortByThisColumn={sortBy === 'author'}
            >
              {t('documentsTable.head3')}
            </Table.Cell>
            <Table.Cell
              onClick={() => handleSortClick('updatedAt')}
              sortDir={sortDir}
              sortByThisColumn={sortBy === 'updatedAt'}
            >
              {t('documentsTable.head4')}
            </Table.Cell>
            <Table.Cell
              className={styles.actionCell}
              aria-label={t('documentsTable.head5Aria') ?? ''}
            />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {materialsTableData.map((material) => (
            <Table.Row key={material?.materialId} isClickable>
              <Table.Cell className={styles.name} isLead>
                <Link
                  to={{
                    pathname: `/${OfficeName.BACK}/materials/${material?.materialId}`,
                    search: `pid=${guidelineId}`,
                  }}
                  aria-label={material?.name + ', go to detail'}
                />
                <FileFormatIcon
                  mimeType={material.type}
                  name={material.name}
                  avatarProps={{
                    size: 'medium',
                  }}
                />
                <Typography>{material?.name}</Typography>
              </Table.Cell>
              <Table.Cell>
                {getMaterialTypeAsString(material.type, material.name)}
              </Table.Cell>
              <Table.Cell>
                <div className={styles.contentAuthor}>
                  <Avatar
                    content={material?.initials}
                    size="small"
                    src={material?.avatar_public_path}
                  />
                  {material?.author}
                </div>
              </Table.Cell>
              <Table.Cell>
                {material?.updatedAt
                  ? formatDateNumeric(material?.updatedAt)
                  : ''}
              </Table.Cell>
              <Table.Cell innerClassName={styles.actionCell}>
                {material?.materialId && (
                  <>
                    {!isFrontOffice && (
                      <MaterialsMorePopover
                        materialId={material?.materialId}
                        fileId={material.fileId}
                        materialType={material?.type}
                        onMaterialRename={(newName: string) =>
                          onMaterialRename(material.materialId, newName)
                        }
                        onMaterialDelete={() =>
                          onMaterialDelete(material.materialId)
                        }
                        guidelineId={guidelineId}
                        className={styles.optionsButton}
                      />
                    )}
                  </>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  )
}

export default MaterialsTable
